import PropTypes from 'prop-types';
import {Dropdown} from '@/components/atoms';
import {PageContainer} from '@/components/organisms';
import {ActivitiesSelector} from '@/containers/ClubPlus/CreateTier/phases/Setup/ActivitiesSelector';
import {useFormContext, Controller} from 'react-hook-form';
import {useQuery} from '@tanstack/react-query';
import {
  getTierOptions,
  TIER_QUERY_KEY,
  getTierDetails
} from '@/services/ClubPlus/TierService';
import {useIntl} from 'react-intl';
import {SectionTitle} from '@/components/molecules/index.jsx';
import {FONT_SIZE} from '@/utils/constants/globalStyle.js';
import {useState} from 'react';
import {ConfirmationModal} from '@/components/molecules';
import {transformTierDataToForm} from '@/services/ClubPlus/tierFormTransform';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import './setup.scss';
import {logger} from '@/helpers/logger';

export const Setup = ({isEditingExistingTier = false}) => {
  const {formatMessage} = useIntl();
  const {control, setValue, watch, reset} = useFormContext();
  const countryCode = useAppSelector(selectCountryCode);
  const [draftExistsModal, setDraftExistsModal] = useState(false);
  const [draftData, setDraftData] = useState(null);

  const {
    data: tierOptionsData,
    isLoading,
    error
  } = useQuery({
    queryKey: [TIER_QUERY_KEY.TIER_OPTIONS],
    queryFn: getTierOptions
  });

  const tierOptions = tierOptionsData?.tierNames || [];
  const activities = tierOptionsData?.activities || [];

  const dropdownOptions =
    tierOptions.length > 0
      ? tierOptions.map((option) => ({
          id: option.id,
          displayText: option.name
        }))
      : [];

  const currentName = watch('name');
  const currentTierId = watch('tierId');

  const getCurrentTierSelection = () => {
    if (currentName && currentTierId && dropdownOptions.length > 0) {
      const matchingOption = dropdownOptions.find(
        (option) =>
          option.id == currentTierId || option.displayText === currentName
      );
      return matchingOption ? [matchingOption] : [];
    }
    return [];
  };

  const handleTierSelect = async (value) => {
    if (isEditingExistingTier) return;

    const selectedTier = value?.[0];
    if (selectedTier) {
      setValue('name', selectedTier.displayText);
      setValue('tierId', selectedTier.id);

      try {
        const draft = await getTierDetails(selectedTier.id, 'status=DRAFT');
        if (draft) {
          setDraftData(draft);
          setDraftExistsModal(true);
        }
        logger.log('draft', draft);
      } catch {
        // No draft found, proceeding with fresh setup.
      }
    } else {
      setValue('name', '');
      setValue('tierId', '');
      setValue('activities', [{activityID: '', multiplier: 0}]);
    }
  };

  const handleUseDraft = () => {
    setDraftExistsModal(false);
    const formData = transformTierDataToForm(
      draftData,
      formatMessage,
      countryCode
    );
    reset(formData);
  };

  const handleStartFresh = () => {
    setDraftExistsModal(false);
  };

  return (
    <section className='mb-80 mt-10'>
      <PageContainer>
        <div className='mb-3'>
          <SectionTitle
            fontSize={FONT_SIZE.XXL}
            label={formatMessage({
              id: 'CLUB_PLUS.TIER_CREATE_SETUP_SECTION_TITLE'
            })}
          />
        </div>
        {error && (
          <div className='mb-4 text-red-500'>Failed to load tier options</div>
        )}
        <Controller
          name={'name'}
          control={control}
          render={() => (
            <Dropdown
              label='Tier Name*'
              placeholder={formatMessage({
                id: 'CLUB_PLUS.TIER_CREATE_NAME_PLACEHOLDER'
              })}
              isJsonWrap={false}
              isLoading={isLoading}
              childList={dropdownOptions}
              preventTyping={true}
              disabled={isEditingExistingTier}
              value={getCurrentTierSelection()}
              updateValue={handleTierSelect}
            />
          )}
        />
        <div className='my-5 w-full border border-gray-700' />
        <ActivitiesSelector
          key={JSON.stringify(watch('activities'))}
          activities={activities}
        />
        <ConfirmationModal
          showModal={draftExistsModal}
          setShowModal={setDraftExistsModal}
          handleConfirm={handleUseDraft}
          title='Draft Version Found'
          text='A draft version of this tier exists. Do you want to continue editing from the draft or start fresh?'
          primaryButtonText='Use Draft'
          type='fluid'
          secondaryButtonProps={{
            onClick: handleStartFresh,
            size: 'medium',
            text: 'Cancel',
            variant: 'secondary'
          }}
        />
      </PageContainer>
    </section>
  );
};

Setup.propTypes = {
  isEditingExistingTier: PropTypes.bool
};
